import { useUser } from "@/network/useUser";
import { format } from "date-fns";
import { useMemo } from "react";
import {
  AsyncMessageTypes,
  type ChatMessage,
  type ChatUserMap,
  MessageModelType,
  MessageTag,
} from "../types/chatMessageTypes";
import { isSystemMessage } from "../utils/messageTypeUtils";

// Interface for date-grouped messages
export interface DateGroup {
  date: string; // Formatted date string (MM/dd/YY)
  senderGroups: SenderGroup[];
}

// Interface for sender-grouped messages
export interface SenderGroup {
  senderId: string;
  senderTag: string;
  messages: ChatMessage[];
}

// Helper function to format date to MM/dd/YY
const formatDateForGrouping = (date: Date): string => {
  return format(date, "MM/dd/yy");
};

/**
 * Custom hook for grouping chat messages by date and then by sender
 *
 * @param messages - Array of chat messages to group
 * @param userMap - Map of users to derive senderTag for tag-less AsyncMessages
 * @returns Array of date groups, each containing sender groups with messages
 */
export const useMessageGrouping = (
  messages: ChatMessage[] = [],
  userMap: ChatUserMap = {},
) => {
  const { data: currentUser } = useUser();
  // Group messages by date and sender
  return useMemo(() => {
    // Use a Map to group by formatted date string
    const dateGroupsMap = new Map<string, DateGroup>();

    // Group messages by date
    for (const message of messages) {
      const messageDate = new Date(message.created);
      const formattedDate = formatDateForGrouping(messageDate);

      // Find or create date group
      if (!dateGroupsMap.has(formattedDate)) {
        dateGroupsMap.set(formattedDate, {
          date: formattedDate,
          senderGroups: [],
        });
      }

      // We know this exists because we just created it if it didn't
      const dateGroup = dateGroupsMap.get(formattedDate) as DateGroup;

      //Check for automation flag first
      if (message.automation) {
        const senderId = "AUTOMATED";
        const senderTag = MessageTag.AUTOMATED;
        const lastSenderGroup =
          dateGroup.senderGroups[dateGroup.senderGroups.length - 1];

        if (
          lastSenderGroup &&
          lastSenderGroup.senderId === senderId &&
          lastSenderGroup.senderTag === senderTag
        ) {
          lastSenderGroup.messages.push(message);
        } else {
          dateGroup.senderGroups.push({
            senderId,
            senderTag,
            messages: [message],
          });
        }
        continue; // Skip other checks if it's automated
      }

      // Determine if this is a system message
      // We have to explicitly check for AsyncMessageTypes.TEXT because AsyncMessages
      // don't have a tag :(
      if (isSystemMessage(message)) {
        dateGroup.senderGroups.push({
          senderId: `system-${message.uuid}`,
          senderTag: MessageTag.SYSTEM,
          messages: [message],
        });
        continue;
      }

      // For user messages, try to find an existing sender group or create a new one
      const senderId = message.userId?.toString() || `unknown-${message.uuid}`;

      let senderTag: string;
      if (message.tag) {
        senderTag = message.tag;
      } else if (
        message.modelType === MessageModelType.ASYNC_MESSAGE &&
        (message.type === AsyncMessageTypes.TEXT ||
          message.type === AsyncMessageTypes.PHOTO) &&
        message.userId
      ) {
        // Handle tag-less Async Text messages using userMap
        const user = userMap[message.userId];
        if (
          user?.professional ||
          (!user && message.userId === currentUser?.userId)
        ) {
          // Someones the user is not in the userMap, we can check if we are the user
          senderTag = MessageTag.PROFESSIONAL;
        } else {
          senderTag = MessageTag.CUSTOMER; // Default to customer if user exists but no professional field, or if user not found
        }
      } else {
        // Fallback for other tag-less messages (should be rare)
        senderTag = MessageTag.UNKNOWN;
      }

      // Find the last sender group in this date group
      const lastSenderGroup =
        dateGroup.senderGroups[dateGroup.senderGroups.length - 1];

      // Check if this message should be added to the previous group
      if (
        lastSenderGroup &&
        lastSenderGroup.senderId === senderId &&
        lastSenderGroup.senderTag === senderTag &&
        lastSenderGroup.senderTag !== MessageTag.SYSTEM
      ) {
        // Add to existing group
        lastSenderGroup.messages.push(message);
      } else {
        // Create a new group
        dateGroup.senderGroups.push({
          senderId,
          senderTag,
          messages: [message],
        });
      }
    }

    // Convert Map to array and sort by date
    const dateGroups = Array.from(dateGroupsMap.values());

    // Sort date groups by date
    dateGroups.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });

    // Sort messages within each sender group by creation time
    for (const dateGroup of dateGroups) {
      for (const senderGroup of dateGroup.senderGroups) {
        senderGroup.messages.sort((a, b) => {
          const timeA = new Date(a.created).getTime();
          const timeB = new Date(b.created).getTime();
          return timeA - timeB;
        });
      }
    }

    // Reverse the order of date groups so newest dates appear first
    dateGroups.reverse();

    return dateGroups;
  }, [messages, userMap, currentUser?.userId]);
};
